import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  findGetParameter,
  getData,
} from "../../components/utilities/UtilityFunctions";

import { handlePagingAll } from "../../components/api/APIUtilities";
import { postNotification } from "../../components/utilities/Notifications";
import LeagueRankingCard from "../../components/layout/LeagueRankingCard";
import ListResponsive from "../../components/other/ListResponsive";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function LeaderboardsTruncated(props) {
  const { leagueId } = useParams();
  const { setActiveTab, embed, region, setLeague, league, setPrevLeague } =
    props;

  const leagueIdParam = findGetParameter("league");

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation("", { keyPrefix: "pages.leaderboards" });

  const [subLoading, setSubLoading] = useState(true);
  const [rankingGroups, setRankingGroups] = useState(null);

  const compareLeagueId =
    league?.id || props.leagueId || leagueId || leagueIdParam;

  useEffect(() => {
    const go = async () => {
      try {
        let apiLeague = league;

        if (!league || (league && league?.id !== compareLeagueId)) {
          const params = {
            active: true,
            "expand[]": ["gameSystem", "gameStore", "gameStoreUser"],
          };

          if (region) {
            params.regionId = region.id;
          }
          apiLeague = await getData(
            `/leagues/${props.leagueId || leagueId}`,
            params
          );

          if (apiLeague.photoFileId) {
            apiLeague.photo = await getData(`/files/${apiLeague.photoFileId}`, {
              queryStringParameters: {},
            });
          }

          setLeague(apiLeague);
        } else {
          if (
            apiLeague.hidePlacings &&
            authContext?.authState?.identity?.isAdmin !== true &&
            !authContext?.authState?.identity?.adminGameSystems?.includes(
              apiLeague?.gameSystemId
            )
          ) {
            if (
              !apiLeague.gameStoreUser?.active ||
              (!apiLeague.gameStoreUser?.owner &&
                !apiLeague.gameStoreUser?.permissions?.createLeagues)
            ) {
              let error = new Error(t("circuitOwnerHiddenRankings"));
              error.name = "HidePlacings Error";
              throw error;
            }
          }

          var aRankingGroups = [
            {
              ...apiLeague,
              name: `${apiLeague.name}${t("rankingsSuffix")}`,
              isLeague: true
            }
          ];

          if (
            apiLeague.gameSystem?.allowsTeamEvent &&
            apiLeague.defaultPlacingType !== "ELO Points"
          ) {
            aRankingGroups.push({
              ...apiLeague,
              name: t("teamRankings"),
              teams: true,
              isLeague: true,
            });
          }
          const tempRankingGroups = [];
          aRankingGroups.forEach((rankingGroup) => {
            tempRankingGroups.push({
              league: rankingGroup,
            });
          });

          if (
            apiLeague.gameSystem?.hasFactions &&
            apiLeague.defaultPlacingType !== "ELO Points"
          ) {
            let apiFactions = await handlePagingAll(`/armies`, {
              limit: 100,
              gameType: apiLeague.gameSystem.code,
              [apiLeague.itc ? "itcFaction" : "leagueFaction"]: true,
            });

            apiFactions.sort((a, b) => {
              if (a.rankingSortOrder !== b.rankingSortOrder) {
                if (a.rankingSortOrder === undefined) return 1;
                if (b.rankingSortOrder === undefined) return -1;
                if (a.rankingSortOrder < b.rankingSortOrder) return -1;
                if (a.rankingSortOrder > b.rankingSortOrder) return 1;
              }

              if (!a.name && !b.name) return 0;
              if (!a.name) return -1;
              if (!b.name) return 1;

              const aVal = a.name.toUpperCase();
              const bVal = b.name.toUpperCase();

              if (aVal < bVal) return -1;
              if (aVal > bVal) return 1;
              return 0;
            });

            let delay = 0;
            apiFactions = apiFactions.map((rankingGroup) => {
              delay += 0.1;
              return {
                ...apiLeague,
                ...rankingGroup,
                topX: rankingGroup.topX || 3,
                delay: delay,
                isFaction: true,
              };
            });

            const tempFactionGroups = [];
            apiFactions.forEach((rankingGroup) => {
              tempFactionGroups.push({
                league: rankingGroup,
              });
            });

            setRankingGroups([...tempRankingGroups, ...tempFactionGroups]);
          } else {
            setRankingGroups(tempRankingGroups);
          }
        }
      } catch (err) {
        if (err.name === "HidePlacings Error") {
          postNotification("warning", err.message);
        } else {
          postNotification("error", err);
        }
      } finally {
        setSubLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, [league]);

  // if (!league) {
  //   return <></>;
  // }

  return (
    <ListResponsive
      name={t("leaderboards")}
      originalList={subLoading ? null : rankingGroups ? rankingGroups : []}
      rowProps={{
        mapFunction: (row, index) => (
          <Grid
            key={index}
            item
            xs={12}
            sm={row?.league?.isLeague ? 12 : 6}
            md={row?.league?.isLeague ? 6 : 4}
            sx={{ p: 1 }}
          >
            <LeagueRankingCard
              ranking={row}
              placingsType={
                row?.league?.isFaction
                  ? "army"
                  : row?.league?.teams
                    ? "team"
                    : row?.league.defaultPlacingType === "ELO Points"
                      ? "elo"
                      : "player"
              }
              region={region}
              onClick={
                IS_GW
                  ? null
                  : () => {
                      if (row?.league?.teams) {
                        setActiveTab("teams");
                        navigate(
                          `/leaderboards/teams?league=${
                            props?.leagueId || props?.league?.id || leagueId
                          }${embed ? "&embed=true" : ""}`
                        );
                        setPrevLeague(league);
                        setLeague(row.league);
                      } else {
                        if (row?.league?.isFaction) {
                          setActiveTab("army");
                        } else {
                          setActiveTab("players");
                        }
                        navigate(
                          `/leaderboards/players?league=${
                            props?.leagueId ||
                            props?.league?.id ||
                            (!row?.league?.isFaction
                              ? row?.league?.id
                              : leagueId)
                          }${
                            row?.league?.isFaction
                              ? `&army=${row.league?.id}`
                              : ""
                          }${embed ? "&embed=true" : ""}`
                        );
                        setPrevLeague(league);
                        setLeague(row.league);
                      }
                      window.scrollTo(0, 0);
                    }
              }
              href={
                IS_GW
                  ? `/leaderboards${
                      row?.league?.teams
                        ? `/teams?league=${
                            props?.leagueId || props?.league?.id || leagueId
                          }`
                        : `/players?league=${
                            props?.leagueId ||
                            props?.league?.id ||
                            (!row?.league?.isFaction
                              ? row?.league?.id
                              : leagueId)
                          }${
                            row?.league?.isFaction
                              ? `&army=${row.league?.id}`
                              : ""
                          }`
                    }${embed ? "&embed=true" : ""}`
                  : null
              }
              leagueId={leagueId}
            />
          </Grid>
        ),
      }}
    />
  );
}
